import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from '../api/axios';
import { useAuth } from '../context/AuthContext';
import SignaturePad from '../components/SignaturePad';
import {
  Container,
  Box,
  Typography,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ExpandMore as ExpandMoreIcon,
  ThumbUp as ApproveIcon,
  ThumbDown as RejectIcon,
  PictureAsPdf as PdfIcon,
  History as HistoryIcon,
  Compare as CompareIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import RevisionDiffDialog from '../components/RevisionDiffDialog';

const EstimateReview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { effectiveRole } = useAuth();

  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [rejectDialog, setRejectDialog] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [revisions, setRevisions] = useState([]);
  const [revisionsDialog, setRevisionsDialog] = useState(false);
  const [diffOpen, setDiffOpen] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [confirmDeleteDialog, setConfirmDeleteDialog] = useState({ open: false, itemId: null });

  const fetchEstimate = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/estimates/${id}`);
      setEstimate(response.data.estimate);
    } catch (error) {
      setError('Failed to load estimate');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchEstimate();
  }, [fetchEstimate]);

  // Per-item approval handlers
  const handleApproveSelected = () => {
    if (selectedItems.length === 0) {
      setError('Please select items to approve');
      return;
    }
    setSignatureDialog(true);
  };

  const handleConfirmApprovalWithSignature = async () => {
    if (selectedItems.length === 0) {
      setError('Please select items to approve');
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.put(`/api/estimates/${id}/items/approve`, {
        item_ids: selectedItems,
        signature: signature
      });

      if (response.data.all_approved) {
        setSignatureDialog(false);
        navigate('/', { state: { successMessage: 'All items approved! This estimate is now an active project.' } });
      } else {
        setSignatureDialog(false);
        setSelectedItems([]);
        fetchEstimate();
        setError('');
        setSuccessMessage(`${response.data.approved_count} of ${response.data.total_count} items approved`);
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to approve items');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejectSelected = async () => {
    if (selectedItems.length === 0) {
      setError('Please select items to reject');
      return;
    }

    setRejectDialog(true);
  };

  const handleConfirmRejectItems = async () => {
    setSubmitting(true);
    try {
      await axios.put(`/api/estimates/${id}/items/reject`, {
        item_ids: selectedItems,
        rejection_reason: rejectionReason
      });

      setRejectDialog(false);
      setRejectionReason('');
      setSelectedItems([]);
      fetchEstimate(); // Refresh to show updated states
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to reject items');
      console.error('Error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    if (!estimate || !estimate.rooms) return;

    const allItemIds = estimate.rooms.flatMap(room =>
      room.items
        .filter(item => item.homeowner_approval !== 'approved' && item.homeowner_approval !== 'rejected')
        .map(item => item.id)
    );

    if (selectedItems.length === allItemIds.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(allItemIds);
    }
  };

  const handleDeleteItem = (itemId) => {
    setConfirmDeleteDialog({ open: true, itemId });
  };

  const handleConfirmDelete = async () => {
    const itemId = confirmDeleteDialog.itemId;
    setConfirmDeleteDialog({ open: false, itemId: null });
    try {
      await axios.delete(`/api/items/${itemId}`);
      fetchEstimate();
      setSelectedItems(prev => prev.filter(id => id !== itemId));
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleApproveAll = () => {
    if (!estimate || !estimate.rooms) return;

    const allItemIds = estimate.rooms.flatMap(room =>
      room.items
        .filter(item => item.homeowner_approval !== 'approved' && item.homeowner_approval !== 'rejected')
        .map(item => item.id)
    );

    if (allItemIds.length === 0) {
      setError('No items to approve');
      return;
    }

    setSelectedItems(allItemIds);
    setSignatureDialog(true);
  };

  const getSubtotal = () => {
    if (!estimate || !estimate.rooms) return 0;
    return estimate.rooms.reduce((total, room) => {
      const roomTotal = room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0);
      return total + roomTotal;
    }, 0);
  };

  const getTaxAmount = () => {
    const subtotal = getSubtotal();
    const taxRate = parseFloat(estimate?.tax_rate || 0);
    return (subtotal * taxRate) / 100;
  };

  const getTotalCost = () => {
    return getSubtotal() + getTaxAmount();
  };

  const getTotalItems = () => {
    if (!estimate || !estimate.rooms) return 0;
    return estimate.rooms.reduce((total, room) => total + room.items.length, 0);
  };

  const getCostByType = () => {
    if (!estimate || !estimate.rooms) return { labor: 0, materials: 0, both: 0 };

    return estimate.rooms.reduce((totals, room) => {
      room.items.forEach(item => {
        const cost = parseFloat(item.cost || 0);
        totals[item.type] = (totals[item.type] || 0) + cost;
      });
      return totals;
    }, { labor: 0, materials: 0, both: 0 });
  };

  const handleDownloadPDF = () => {
    // Open print view in new window - user can save as PDF from browser
    window.open(`/estimate-print/${id}`, '_blank');
  };

  const fetchRevisions = async () => {
    try {
      const response = await axios.get(`/api/estimates/${id}/revisions`);
      setRevisions(response.data.revisions);
      setRevisionsDialog(true);
    } catch (error) {
      setError('Failed to load revision history');
      console.error('Error:', error);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!estimate) {
    return (
      <Container>
        <Alert severity="error">Estimate not found</Alert>
      </Container>
    );
  }

  const costBreakdown = getCostByType();
  const canApproveReject = estimate.status === 'pending_approval' && effectiveRole === 'homeowner';

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static" sx={{ backgroundColor: 'white', boxShadow: 2 }}>
        <Toolbar>
          <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 2, color: 'primary.main' }} aria-label="Go back">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" color="primary">
              {estimate.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              From {estimate.contractor_first_name} {estimate.contractor_last_name}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<CompareIcon />}
              onClick={() => setDiffOpen(true)}
            >
              What changed
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<HistoryIcon />}
              onClick={fetchRevisions}
            >
              Revisions
            </Button>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PdfIcon />}
              onClick={handleDownloadPDF}
            >
              Download PDF
            </Button>
            {canApproveReject && (
              <>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleApproveAll}
                  disabled={submitting}
                  sx={{ mr: 2 }}
                >
                  Approve All
                </Button>
                <Chip
                  label={`${selectedItems.length} selected`}
                  color="primary"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={handleRejectSelected}
                  disabled={submitting || selectedItems.length === 0}
                >
                  Reject Selected
                </Button>
                <Button
                  variant="outlined"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={handleApproveSelected}
                  disabled={submitting || selectedItems.length === 0}
                >
                  Approve Selected
                </Button>
              </>
            )}
          </Box>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage('')}>
            {successMessage}
          </Alert>
        )}

        {canApproveReject && (
          <Paper sx={{ p: 2, mb: 3, backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Alert severity="info" sx={{ flex: 1, mr: 2 }} icon={false}>
                <strong>Review each item carefully.</strong> Select items to approve or reject them individually.
                When all items are approved, the estimate becomes an active project.
              </Alert>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedItems.length > 0 && selectedItems.length === estimate.rooms?.flatMap(r => r.items.filter(item => item.homeowner_approval !== 'approved' && item.homeowner_approval !== 'rejected')).length}
                    indeterminate={selectedItems.length > 0 && selectedItems.length < estimate.rooms?.flatMap(r => r.items.filter(item => item.homeowner_approval !== 'approved' && item.homeowner_approval !== 'rejected')).length}
                    onChange={handleSelectAll}
                  />
                }
                label="Select All"
              />
            </Box>
          </Paper>
        )}

        {/* Estimate Summary */}
        <Paper sx={{ p: 4, mb: 4, backgroundColor: 'rgba(25, 118, 210, 0.05)' }}>
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Estimate Summary
          </Typography>
          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-around" flexWrap="wrap" gap={3} mt={3}>
            <Box textAlign="center">
              <Typography variant="h3" color="primary" fontWeight="bold">
                ${getTotalCost().toLocaleString()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Project Cost
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h3" fontWeight="bold">
                {getTotalItems()}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Items
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography variant="h3" fontWeight="bold">
                {estimate.rooms?.length || 0}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Rooms/Work Areas
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Cost Breakdown */}
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Cost Breakdown</Typography>
          <Box display="flex" gap={3} mt={2} mb={3}>
            {costBreakdown.labor > 0 && (
              <Box>
                <Chip label="Labor" color="primary" size="small" sx={{ mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  ${costBreakdown.labor.toLocaleString()}
                </Typography>
              </Box>
            )}
            {costBreakdown.materials > 0 && (
              <Box>
                <Chip label="Materials" color="info" size="small" sx={{ mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  ${costBreakdown.materials.toLocaleString()}
                </Typography>
              </Box>
            )}
            {costBreakdown.both > 0 && (
              <Box>
                <Chip label="Labor & Materials" color="success" size="small" sx={{ mb: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  ${costBreakdown.both.toLocaleString()}
                </Typography>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" flexDirection="column" alignItems="flex-end" gap={1}>
            <Box display="flex" justifyContent="space-between" width="100%" maxWidth={400}>
              <Typography variant="body1">Subtotal:</Typography>
              <Typography variant="body1" fontWeight="bold">
                ${getSubtotal().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
            {parseFloat(estimate?.tax_rate || 0) > 0 && (
              <Box display="flex" justifyContent="space-between" width="100%" maxWidth={400}>
                <Typography variant="body1">Tax ({parseFloat(estimate?.tax_rate || 0).toFixed(2)}%):</Typography>
                <Typography variant="body1" fontWeight="bold">
                  ${getTaxAmount().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Typography>
              </Box>
            )}
            <Box display="flex" justifyContent="space-between" width="100%" maxWidth={400} sx={{
              borderTop: 2,
              borderColor: 'primary.main',
              pt: 1,
              mt: 1
            }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6" color="primary" fontWeight="bold">
                ${getTotalCost().toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Rooms and Items */}
        <Typography variant="h5" gutterBottom>Detailed Breakdown</Typography>

        {estimate.rooms && estimate.rooms.length > 0 ? (
          estimate.rooms.map((room) => (
            <Accordion key={room.id} defaultExpanded sx={{ mb: 2 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" justifyContent="space-between" width="100%">
                  <Box>
                    <Typography variant="h6">{room.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {room.items.length} items
                    </Typography>
                  </Box>
                  <Typography variant="h6" color="primary" fontWeight="bold">
                    ${room.items.reduce((sum, item) => sum + parseFloat(item.cost || 0), 0).toLocaleString()}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Box>
                  {room.items.map((item, index) => {
                    const isApproved = item.homeowner_approval === 'approved';
                    const isRejected = item.homeowner_approval === 'rejected';
                    const isSelected = selectedItems.includes(item.id);

                    return (
                      <Box key={item.id}>
                        <Paper
                          sx={{
                            p: 2,
                            mb: 2,
                            backgroundColor: isApproved ? 'rgba(76, 175, 80, 0.1)' : isRejected ? 'rgba(244, 67, 54, 0.1)' : 'white',
                            border: isSelected ? '2px solid #1976d2' : '1px solid rgba(0, 0, 0, 0.12)'
                          }}
                          variant="outlined"
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center">
                            {canApproveReject && !isApproved && !isRejected && (
                              <Checkbox
                                checked={isSelected}
                                onChange={() => handleToggleItem(item.id)}
                                sx={{ mr: 2 }}
                              />
                            )}
                            <Box flex={1}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {item.title}
                              </Typography>
                              <Box display="flex" gap={1} mt={1}>
                                <Chip
                                  label={item.type}
                                  size="small"
                                  color={
                                    item.type === 'labor' ? 'primary' :
                                    item.type === 'materials' ? 'info' : 'success'
                                  }
                                />
                                {isApproved && <Chip label="Approved" size="small" color="success" />}
                                {isRejected && <Chip label="Rejected" size="small" color="error" />}
                              </Box>
                            </Box>
                            <Box display="flex" alignItems="center" gap={2}>
                              <Typography variant="h6" color="primary" fontWeight="bold">
                                ${parseFloat(item.cost).toLocaleString()}
                              </Typography>
                              {estimate.status === 'pending_approval' && (
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteItem(item.id)}
                                  aria-label={`Delete item: ${item.title || 'item'}`}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              )}
                            </Box>
                          </Box>
                        </Paper>
                      </Box>
                    );
                  })}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))
        ) : (
          <Alert severity="warning">
            This estimate has no items yet.
          </Alert>
        )}

        {/* Action Buttons for Mobile */}
        {canApproveReject && (
          <Box display="flex" gap={2} mt={4} justifyContent="center">
          </Box>
        )}
      </Container>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Selected Items</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            You are rejecting {selectedItems.length} item(s). Please provide feedback to help the contractor revise.
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection (Optional)"
            fullWidth
            variant="outlined"
            multiline
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="e.g., Cost too high, different material needed, remove this item, etc."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmRejectItems}
            variant="contained"
            color="error"
            disabled={submitting}
          >
            {submitting ? 'Rejecting...' : 'Reject Items'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Revision History Dialog */}
      <Dialog open={revisionsDialog} onClose={() => setRevisionsDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Revision History</DialogTitle>
        <DialogContent>
          {revisions.length === 0 ? (
            <Alert severity="info">No revisions yet. This estimate hasn't been submitted.</Alert>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Track changes and cost differences between estimate submissions.
              </Typography>
              {revisions.map((revision, index) => (
                <Paper key={revision.id} sx={{ p: 2, mb: 2 }} variant="outlined">
                  <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                      <Typography variant="h6">
                        Revision {revision.revision_number}
                        {index === 0 && <Chip label="Current" color="primary" size="small" sx={{ ml: 1 }} />}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Submitted: {new Date(revision.submitted_at).toLocaleString()}
                      </Typography>
                      {revision.notes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          {revision.notes}
                        </Typography>
                      )}
                    </Box>
                    <Box textAlign="right">
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        ${parseFloat(revision.total_cost).toLocaleString()}
                      </Typography>
                      {index < revisions.length - 1 && (
                        <Typography
                          variant="caption"
                          color={
                            parseFloat(revision.total_cost) > parseFloat(revisions[index + 1].total_cost)
                              ? 'error'
                              : 'success'
                          }
                        >
                          {parseFloat(revision.total_cost) > parseFloat(revisions[index + 1].total_cost) ? '+' : ''}
                          ${(parseFloat(revision.total_cost) - parseFloat(revisions[index + 1].total_cost)).toLocaleString()}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevisionsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={confirmDeleteDialog.open}
        onClose={() => setConfirmDeleteDialog({ open: false, itemId: null })}
      >
        <DialogTitle>Delete Item</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this item from the estimate?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteDialog({ open: false, itemId: null })}>
            Cancel
          </Button>
          <Button onClick={handleConfirmDelete} variant="contained" color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Signature Dialog */}
      <Dialog open={signatureDialog} onClose={() => !submitting && setSignatureDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Sign to Approve Selected Items</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 3 }}>
            By signing, you agree to approve the {selectedItems.length} selected item(s).
            {selectedItems.length > 0 && ' If all items are approved, this estimate will convert to an active project.'}
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please sign below to confirm your approval:
          </Typography>
          <SignaturePad onSignatureChange={setSignature} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSignatureDialog(false)} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmApprovalWithSignature}
            variant="contained"
            color="success"
            disabled={submitting || !signature}
          >
            {submitting ? 'Approving...' : 'Approve with Signature'}
          </Button>
        </DialogActions>
      </Dialog>

      <RevisionDiffDialog
        open={diffOpen}
        onClose={() => setDiffOpen(false)}
        estimateId={id}
      />
    </Box>
  );
};

export default EstimateReview;
